/**
 * Admin auth client — direct sign-in, session lookup, and revocation.
 *
 * The Admin SPA posts credentials to the existing cookie-mode endpoint. The
 * resulting HttpOnly coach_session cookie stays on the API origin and every
 * subsequent request includes it through the shared transport.
 *
 * This was the eighth hand-rolled client (ADR-0056), and the one a
 * check scoped to `src/**​/api.ts` would never have caught — which is why
 * the guard spec looks at all of `src/**`.
 *
 * It moves onto the shared transport but its result stays in **Jotai**,
 * not in a query. The session is not server state we poll and invalidate;
 * it is what decides whether any query may run at all. See
 * `use-admin-auth.ts`.
 */
import { AdminApiError, request } from '../api/client.js'
import type { AdminUser } from '../state/atoms.js'

interface MeResponse {
  isInternal?: boolean
  id: string
  clientId: string
  email: string
  name: string
  role: AdminUser['role']
  tier: string | null
}

interface LoginResponse {
  user: MeResponse
  expiresAt: string
}

function toAdminUser(body: MeResponse): AdminUser {
  return {
    id: body.id,
    clientId: body.clientId,
    email: body.email,
    name: body.name,
    role: body.role,
    isInternal: body.isInternal,
  }
}

export const authClient = {
  async login(email: string, password: string): Promise<AdminUser> {
    const body = await request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
    return toAdminUser(body.user)
  },

  async me(): Promise<AdminUser> {
    const body = await request<MeResponse>('/auth/me', { method: 'GET' })
    return toAdminUser(body)
  },

  async logout(): Promise<void> {
    await request<void>('/auth/logout', { method: 'POST' })
  },

  /**
   * Change the operator's own password. Operators cannot use
   * forgot-password, so this is their only self-service way to change it.
   * This session stays signed in; the API signs out every other one.
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await request<void>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    })
  },
}

/**
 * Is this failure "you are not signed in"?
 *
 * A 401 clears known local identity; a network blip or a 500 must not claim
 * the server session ended. Bootstrap still permits direct sign-in when
 * its initial probe fails. Previously the distinction was `AuthClientError`'s
 * `UNAUTHENTICATED` code; it is now the shared transport's, and this helper
 * keeps callers from reaching for the code string directly.
 */
export function isUnauthenticated(err: unknown): boolean {
  return err instanceof AdminApiError && err.code === 'UNAUTHENTICATED'
}
